import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { useCallback } from "react";
import type { Entry, EntryStatus } from "@/shared/types/entries";
import { entryQueryKeys } from "../entryQueryKeys";
import { useCacheSlice } from "../hooks";
import type { ApiClient } from "../types";

export type {
	EntryCodes,
	EntryMatter,
	EntryStatus,
} from "@/shared/types/entries";

/** Stripe-style list envelope returned by the v2 entry read endpoints. */
type ListPage = {
	object: "list";
	url: string;
	hasMore: boolean;
	data: Entry[];
};

export type EntriesDateScope = { from?: string; to?: string };

/**
 * Server-side row scope for entry list reads. Missing filter arrays mean
 * "all," while omitted timekeeperIds uses the backend's caller scope. Present
 * empty arrays intentionally match nothing.
 */
export type EntriesQueryScope = {
	date?: EntriesDateScope;
	statuses?: readonly EntryStatus[];
	timekeeperIds?: readonly string[];
	application?: readonly string[];
	matter?: readonly string[];
	client?: readonly string[];
	billableCategory?: readonly string[];
	/** List pages do not need attachments; edit dialogs fetch them lazily. */
	includeAttachments?: boolean;
	/** Enables role-gated firm-wide reads with explicit timekeeper IDs. */
	adminMode?: boolean;
};

export type EntriesQueryBuildResult = {
	query: Record<string, unknown>;
	matchesNothing: boolean;
};

// "Credit" is retired in the UI: entries on billable_credit matters display
// as non-billable everywhere. Folding happens here, at the fetch boundary, so
// every consumer (table cells, stats, client-side filters) sees one category.
function foldCreditCategory(entry: Entry): Entry {
	return entry.billableCategory === "billable_credit"
		? { ...entry, billableCategory: "non_billable" }
		: entry;
}

// Server rows are still categorized billable_credit, so a non-billable filter
// must also match them or rows that display as non-billable would vanish when
// the user filters by Non-billable.
export function expandBillableCategoryFilter(
	values?: readonly string[],
): readonly string[] | undefined {
	if (!values?.includes("non_billable")) return values;
	if (values.includes("billable_credit")) return values;
	return [...values, "billable_credit"];
}

/**
 * Page size used by the cache loop. Sized to comfortably hold the typical
 * user's full dataset in a single roundtrip — most users come back in one
 * page and skip the cursor entirely. Matches the endpoint's `max` cap.
 */
const PAGE_LIMIT = 5000;

/** Hard ceiling so a misbehaving server can't pin this loop forever. */
const MAX_PAGES = 200;

function sortedUnique(
	values: readonly string[] | undefined,
): string[] | undefined {
	if (values === undefined) return undefined;
	return Array.from(
		new Set(values.map((value) => value.trim()).filter(Boolean)),
	).sort();
}

export function entriesRequestTimekeeperIds(
	scope?: EntriesQueryScope,
): string[] {
	return sortedUnique(scope?.timekeeperIds) ?? [];
}

function addInFilter(
	query: Record<string, unknown>,
	field: string,
	values: readonly string[] | undefined,
): boolean {
	if (values === undefined) return false;
	const normalized = sortedUnique(values) ?? [];
	if (normalized.length === 0) return true;
	query[field] = { $in: normalized };
	return false;
}

export function buildEntriesQuery(
	scope: EntriesQueryScope | undefined,
	options: { includeDate?: boolean } = {},
): EntriesQueryBuildResult {
	const includeDate = options.includeDate ?? true;
	const query: Record<string, unknown> = {};
	let matchesNothing =
		scope?.timekeeperIds !== undefined &&
		(sortedUnique(scope.timekeeperIds)?.length ?? 0) === 0;

	if (includeDate && (scope?.date?.from || scope?.date?.to)) {
		query.date = {
			...(scope.date.from ? { $gte: scope.date.from } : {}),
			...(scope.date.to ? { $lte: scope.date.to } : {}),
		};
	}

	matchesNothing ||= addInFilter(query, "status", scope?.statuses);
	matchesNothing ||= addInFilter(query, "application", scope?.application);
	matchesNothing ||= addInFilter(query, "matter.code", scope?.matter);
	matchesNothing ||= addInFilter(query, "matter.client", scope?.client);
	matchesNothing ||= addInFilter(
		query,
		"billableCategory",
		expandBillableCategoryFilter(scope?.billableCategory),
	);

	return { query, matchesNothing };
}

function normalizedScopeForKey(scope?: EntriesQueryScope) {
	const normalized = {
		date: scope?.date?.from || scope?.date?.to ? scope.date : undefined,
		statuses:
			scope?.statuses === undefined ? undefined : sortedUnique(scope.statuses),
		timekeeperIds:
			scope?.timekeeperIds === undefined
				? undefined
				: sortedUnique(scope.timekeeperIds),
		application:
			scope?.application === undefined
				? undefined
				: sortedUnique(scope.application),
		matter:
			scope?.matter === undefined ? undefined : sortedUnique(scope.matter),
		client:
			scope?.client === undefined ? undefined : sortedUnique(scope.client),
		billableCategory:
			scope?.billableCategory === undefined
				? undefined
				: sortedUnique(scope.billableCategory),
		includeAttachments: scope?.includeAttachments === true,
		adminMode: scope?.adminMode === true,
	};
	return Object.fromEntries(
		Object.entries(normalized).filter(([, value]) => value !== undefined),
	);
}

export function entriesScopeKey(scope?: EntriesQueryScope): string {
	return JSON.stringify(normalizedScopeForKey(scope));
}

/**
 * Walks /v2/entries/query with `startingAfter` until the server reports
 * `hasMore: false`, scoped to explicit timekeepers when provided. Omitting
 * timekeeperIds makes the server use its logged-in-user default.
 */
export async function fetchAllEntries(
	api: ApiClient,
	scope?: EntriesQueryScope,
): Promise<Entry[]> {
	const { query, matchesNothing } = buildEntriesQuery(scope);
	if (matchesNothing) return [];

	const out: Entry[] = [];
	let startingAfter: string | null = null;
	const timekeeperIds = entriesRequestTimekeeperIds(scope);
	for (let page = 0; page < MAX_PAGES; page++) {
		// POST /query until the backend OpenAPI tooling supports the QUERY method.
		const res = await api.makeApiRequest({
			endpoint: "/v2/entries/query",
			method: "POST",
			body: {
				admin: scope?.adminMode === true,
				limit: PAGE_LIMIT,
				includeAttachments: scope?.includeAttachments === true,
				...(timekeeperIds.length > 0 ? { timekeeperIds } : {}),
				...(startingAfter ? { startingAfter } : {}),
				...(Object.keys(query).length > 0 ? { q: query } : {}),
			},
		});
		if (res.status === "ERROR") {
			throw new Error(res.error || "Failed to fetch entries");
		}
		const body = res.data as ListPage | undefined;
		if (!body || !Array.isArray(body.data)) break;
		out.push(...body.data.map(foldCreditCategory));
		if (!body.hasMore || body.data.length === 0) return out;
		startingAfter = body.data[body.data.length - 1].id;
	}
	return out;
}

export function useEntriesCache(
	scope?: EntriesQueryScope,
	options: { enabled?: boolean } = {},
) {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const { data, isLoading, error, lastFetchedAt } = useCacheSlice("entries", {
		scope,
		enabled: options.enabled,
	});
	const entries = data as Entry[];
	// Match the convention used by other slices: only flag initial-load so
	// background refetches don't flicker derived UI.
	const isInitialLoading = isLoading && lastFetchedAt === 0;
	const queryKey = entryQueryKeys.entryList(user?.id, entriesScopeKey(scope));
	const updateEntries = useCallback(
		(updater: (current: Entry[]) => Entry[]) => {
			queryClient.setQueryData<Entry[]>(queryKey, (current) =>
				updater(current ?? []),
			);
		},
		[queryClient, queryKey],
	);
	const refetch = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey, exact: true });
	}, [queryClient, queryKey]);

	return {
		entries,
		isLoading: isInitialLoading,
		error,
		lastFetchedAt,
		updateEntries,
		refetch,
	};
}
